import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  List,
  ListItem,
  ListItemText,
  Divider,
  Grid,
  TextField,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import AnimalInfoForm from '../components/AnimalInfoForm';
import ReportCard from '../components/ReportCard';
import TokenUsageInfo from '../components/TokenUsageInfo';
import StatusAlert from '../components/StatusAlert';
import {
  listAppointments,
  getAppointment,
  updateAppointment,
  deleteAppointment,
  refineAnalysis,
} from '../api/client';
import type { AnimalInfo, Appointment, AnalysisResult, Usage } from '../types';
import { useNavigate } from 'react-router-dom';

export default function HistoryPage() {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [selected, setSelected] = useState<Appointment | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [editedInfo, setEditedInfo] = useState<AnimalInfo>({});
  const [continueText, setContinueText] = useState('');
  const [status, setStatus] = useState<{ message: string; type: 'info' | 'success' | 'warning' | 'error' } | null>(null);
  const [loading, setLoading] = useState(false);

  const loadAppointments = useCallback(async () => {
    try {
      const data = await listAppointments();
      setAppointments(data);
    } catch (err: any) {
      setStatus({ message: 'Ошибка загрузки истории: ' + err.message, type: 'error' });
    }
  }, []);

  useEffect(() => {
    loadAppointments();
  }, [loadAppointments]);

  const handleSelect = useCallback((id: number) => {
    navigate(`/history/${id}`);
  }, [navigate]);

  const handleBack = useCallback(() => {
    setSelected(null);
    setEditMode(false);
    setContinueText('');
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (!selected) return;
    setLoading(true);
    try {
      await updateAppointment(selected.id, {
        ...selected,
        animal_info: editedInfo,
      });
      const updated = await getAppointment(selected.id);
      setSelected(updated);
      setEditMode(false);
      await loadAppointments();
      setStatus({ message: 'Данные о пациенте обновлены.', type: 'success' });
    } catch (err: any) {
      setStatus({ message: 'Ошибка сохранения: ' + err.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [selected, editedInfo, loadAppointments]);

  const handleDelete = useCallback(async () => {
    if (!selected) return;
    if (!confirm('Удалить этот приём?')) return;
    try {
      await deleteAppointment(selected.id);
      setSelected(null);
      await loadAppointments();
      setStatus({ message: 'Приём удалён.', type: 'success' });
    } catch (err: any) {
      setStatus({ message: 'Ошибка удаления: ' + err.message, type: 'error' });
    }
  }, [selected, loadAppointments]);

  const handleContinue = useCallback(async () => {
    if (!selected || !continueText.trim() || selected.reports.length === 0) return;
    setLoading(true);
    setStatus({ message: 'Ассистент обрабатывает запрос...', type: 'info' });
    try {
      const previousReport = selected.reports[selected.reports.length - 1] as AnalysisResult;
      const result = await refineAnalysis(
        selected.transcript,
        selected.animal_info || {},
        previousReport,
        continueText
      );
      result.title = `Продолжение диалога #${selected.reports.length + 1}`;
      const usage = result.usage || { tokens_in: 0, tokens_out: 0, cost: 0, model: '' };
      const totalUsage: Usage = {
        tokens_in: (selected.tokens_in || 0) + usage.tokens_in,
        tokens_out: (selected.tokens_out || 0) + usage.tokens_out,
        cost: (selected.cost || 0) + usage.cost,
        model: usage.model || selected.model || '',
      };
      const newReports = [...selected.reports, result];
      await updateAppointment(selected.id, {
        ...selected,
        reports: newReports,
        ...totalUsage,
      });
      const updated = await getAppointment(selected.id);
      setSelected(updated);
      setContinueText('');
      setStatus({ message: 'Ответ ассистента добавлен в историю.', type: 'success' });
    } catch (err: any) {
      setStatus({ message: 'Ошибка продолжения диалога: ' + err.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [selected, continueText]);

  const formatDate = (iso: string) => {
    const date = new Date(iso);
    return date.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCost = (cost?: number) => {
    if (cost === undefined || cost === null) return '—';
    return '$' + cost.toFixed(6);
  };

  if (selected) {
    return (
      <Box>
        {status && <StatusAlert message={status.message} type={status.type} />}

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
              <Box>
                <Typography variant="h5" gutterBottom>
                  {selected.title || `Приём №${selected.id}`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Дата: {formatDate(selected.created_at)} | Обновлено: {formatDate(selected.updated_at)}
                </Typography>
              </Box>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => setEditMode(!editMode)}
                disabled={editMode}
              >
                Редактировать
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {editMode && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Редактирование данных о пациенте
              </Typography>
              <AnimalInfoForm value={editedInfo} onChange={setEditedInfo} />
              <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <Button variant="contained" onClick={handleSaveEdit} disabled={loading}>
                  Сохранить
                </Button>
                <Button variant="outlined" onClick={() => setEditMode(false)}>
                  Отмена
                </Button>
              </Stack>
            </CardContent>
          </Card>
        )}

        {!editMode && Object.keys(selected.animal_info || {}).filter((k) => (selected.animal_info as any)[k]).length > 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Данные о пациенте
              </Typography>
              <Grid container spacing={2}>
                {Object.entries(selected.animal_info || {})
                  .filter(([_, v]) => v)
                  .map(([k, v]) => (
                    <Grid item xs={12} sm={6} md={4} key={k}>
                      <Typography variant="body2">
                        <strong>{k}:</strong> {String(v)}
                      </Typography>
                    </Grid>
                  ))}
              </Grid>
            </CardContent>
          </Card>
        )}

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Расшифровка
            </Typography>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {selected.transcript}
            </Typography>
          </CardContent>
        </Card>

        {selected.reports.length > 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Заключения
              </Typography>
              {selected.reports.map((report, index) => (
                <ReportCard
                  key={index}
                  report={report}
                  defaultExpanded={index === selected.reports.length - 1}
                />
              ))}
            </CardContent>
          </Card>
        )}

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Продолжить диалог с ассистентом
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Задайте уточняющий вопрос, опишите новые симптомы или результаты анализов.
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={continueText}
              onChange={(e) => setContinueText(e.target.value)}
              placeholder="Например: после приёма собаке дали назначенное лекарство, рвота повторилась..."
              sx={{ mb: 2 }}
            />
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              onClick={handleContinue}
              disabled={!continueText.trim() || loading}
              sx={{ mt: 2 }}
            >
              Отправить ассистенту
            </Button>
          </CardContent>
        </Card>

        <TokenUsageInfo
          usage={{
            tokens_in: selected.tokens_in || 0,
            tokens_out: selected.tokens_out || 0,
            cost: selected.cost || 0,
            model: selected.model || '',
          }}
        />

        <Stack direction="row" spacing={2}>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={handleBack}>
            Назад к списку
          </Button>
          <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={handleDelete}>
            Удалить приём
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box>
      {status && <StatusAlert message={status.message} type={status.type} />}

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            История приёмов
          </Typography>
          {appointments.length === 0 ? (
            <Typography color="text.secondary">
              История приёмов пуста. Создайте первый приём на главной странице.
            </Typography>
          ) : (
            <List>
              {appointments.map((item) => (
                <div key={item.id}>
                  <ListItem
                    button
                    onClick={() => handleSelect(item.id)}
                    sx={{
                      borderRadius: 2,
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <ListItemText
                      primary={item.title || `Приём №${item.id}`}
                      secondary={
                        <Stack direction="row" spacing={2} flexWrap="wrap">
                          <Typography variant="caption">📅 {formatDate(item.created_at)}</Typography>
                          <Typography variant="caption">🤖 {item.model || '—'}</Typography>
                          <Typography variant="caption">🪙 {formatCost(item.cost)}</Typography>
                          <Typography variant="caption">
                            Токены: {item.tokens_in || 0} / {item.tokens_out || 0}
                          </Typography>
                        </Stack>
                      }
                    />
                  </ListItem>
                  <Divider component="li" />
                </div>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
